// Financials — top-level app (QuickBooks read-only phase 1) behind the
// second-password gate. State cascade:
//   Locked (FinGate) → Not connected (Settings CTA) → needsReconnect banner
//   → Healthy (tabs fetch live QBO data via callables).
// No DataProvider — this app reads ZERO client Firestore; everything arrives
// through gated callables and lives in an in-memory cache (useQbo).
// Mounted from src/App.jsx when activeApp === "financials"; routes off the
// SAME navItems array the topbar renders (JobsApp convention).
import React, { useCallback, useEffect, useState } from "react";
import { C, font } from "../../lib/styles.js";
import { finLock, isLockRequired, qboStatus } from "../../lib/qboClient.js";
import { clearUnlock, getUnlock } from "./lib/finSession.js";
import { clearQboCache } from "./lib/useQbo.js";
import FinGate from "./FinGate.jsx";
import Overview from "./views/Overview.jsx";
import Invoices from "./views/Invoices.jsx";
import Expenses from "./views/Expenses.jsx";
import Reports from "./views/Reports.jsx";
import Settings from "./views/Settings.jsx";
import { NotConnected } from "./views/common.jsx";

const fmtCountdown = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

export default function FinancialsApp({
  activeTab, user, navItems, setActiveTab, pendingQboCallback, onQboCallbackConsumed,
}) {
  // Unlock session — seeded from sessionStorage so a reload (or the Intuit
  // OAuth round-trip) doesn't re-prompt inside the 15-minute window.
  const [unlock, setUnlockState] = useState(() => getUnlock());
  const [qbo, setQbo] = useState(null); // qboStatus result
  const [qboLoading, setQboLoading] = useState(false);
  const [now, setNow] = useState(Date.now());

  const relock = useCallback(() => {
    clearUnlock();
    clearQboCache();
    setUnlockState(null);
    setQbo(null);
  }, []);

  // Countdown tick + auto-relock at expiry (server is the real clock; this
  // just keeps the UI honest).
  useEffect(() => {
    if (!unlock) return undefined;
    const id = setInterval(() => {
      setNow(Date.now());
      if (Date.now() >= unlock.expiresAt) relock();
    }, 1000);
    return () => clearInterval(id);
  }, [unlock, relock]);

  // Token comes from component STATE, not storage — in private/restricted
  // browsers the sessionStorage write can fail while the in-memory unlock is
  // perfectly valid (codex review).
  const unlockToken = unlock?.token ?? null;
  const refreshStatus = useCallback(async () => {
    if (!unlockToken) return;
    setQboLoading(true);
    try {
      setQbo(await qboStatus({ unlockToken }));
    } catch (e) {
      if (isLockRequired(e)) relock();
      else setQbo({ connected: false, error: e.message });
    } finally {
      setQboLoading(false);
    }
  }, [unlockToken, relock]);

  // Fetch connection status once per unlock.
  useEffect(() => {
    if (unlockToken) refreshStatus();
  }, [unlockToken, refreshStatus]);

  const doLockNow = useCallback(async () => {
    try { await finLock(); } catch { /* lock is idempotent; local clear matters */ }
    relock();
  }, [relock]);

  // ── Locked ────────────────────────────────────────────────────────────────
  if (!unlock) {
    return (
      <div style={{ padding: "24px 20px", maxWidth: 1100, margin: "0 auto" }}>
        <FinGate onUnlocked={({ token, expiresAt }) => setUnlockState({ token, expiresAt })} />
      </div>
    );
  }

  const ctx = {
    uid: user?._docId,
    connectionId: qbo?.connectionId ?? null,
    unlockToken: unlock.token,
    onLockRequired: relock,
    onConnectionGone: () => refreshStatus(),
  };

  const tab = navItems?.[activeTab] ?? "Overview";
  const settingsIdx = Math.max(0, (navItems ?? []).indexOf("Settings"));
  const goSettings = () => setActiveTab(settingsIdx);

  const settingsEl = (
    <Settings
      ctx={ctx} qbo={qbo} refreshStatus={refreshStatus}
      pendingQboCallback={pendingQboCallback}
      onQboCallbackConsumed={onQboCallbackConsumed}
    />
  );

  let body;
  if (tab === "Settings") {
    body = settingsEl;
  } else if (qboLoading && !qbo) {
    body = <div style={{ color: C.muted, fontSize: 13, textAlign: "center", padding: 40 }}>Checking QuickBooks…</div>;
  } else if (!qbo?.connected) {
    body = <NotConnected onGoSettings={goSettings} />;
  } else if (tab === "Invoices") {
    body = <Invoices ctx={ctx} />;
  } else if (tab === "Expenses") {
    body = <Expenses ctx={ctx} onGoSettings={goSettings} />;
  } else if (tab === "Reports") {
    body = <Reports ctx={ctx} />;
  } else {
    body = <Overview ctx={ctx} onGoSettings={goSettings} />;
  }

  return (
    <div style={{ padding: "18px 20px 60px", maxWidth: 1100, margin: "0 auto", fontFamily: font }}>
      {/* Unlock strip: countdown + explicit lock. */}
      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 10, marginBottom: 14 }}>
        {qbo?.connected && qbo.needsReconnect && tab !== "Settings" && (
          <span style={{
            marginRight: "auto", fontSize: 12, color: "#8a5a06", background: "#FDF3E1",
            border: "1px solid #F3D9A4", borderRadius: 6, padding: "5px 10px",
          }}>
            QuickBooks needs to be reconnected —{" "}
            <a style={{ color: "inherit", cursor: "pointer", textDecoration: "underline" }} onClick={goSettings}>open Settings</a>
          </span>
        )}
        <span style={{
          fontSize: 11, padding: "3px 9px", borderRadius: 20, color: C.brown,
          border: `1px solid ${C.brown}`, fontFamily: font,
        }}>
          🔓 {fmtCountdown(unlock.expiresAt - now)} left
        </span>
        <button style={{
          font: "inherit", fontSize: 12, padding: "5px 12px", borderRadius: 6,
          border: `1px solid ${C.border}`, background: C.surface, cursor: "pointer",
        }} onClick={doLockNow}>Lock now</button>
      </div>
      {body}
    </div>
  );
}
